import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { ReactComponent as DownloadIcon } from 'assets/download-icon.svg';
import { useApplicationContext } from 'contexts/ApplicationContext';
import CommissionsList from '../PaymentTransaction/components/CommissionsList';
import InternalTransactionPartyControl from '../PaymentTransaction/components/InternalTransactionPartyControl';
import BeneficiarySection, { getPartyTypeOptions } from './components/BeneficiarySection';
import RepeatTransactionAsyncSelect from './components/RepeatTransactionAsyncSelect';
import RepeatTransactionSummary from './components/RepeatTransactionSummary';
import RequisitesSection, { getRequisitesKindOptions } from './components/RequisitesSection';
import SectionSwitch from './components/SectionSwitch';
import {
  createCustomCommission,
  fromErrorsDto,
  fromRepeatDto,
  getBankCountry,
  isAdvancedPaymentMethod,
  isExternalPaymentMethod,
  toDto
} from './components/RepeatTransactionModel';
import {
  AMOUNT_INPUT_VALUE_TYPE,
  COMMON_CURRENCY_PRECISION,
  CRYPTO_CURRENCY_PRECISION,
  MAX_TRANSACTION_COMMENT_LENGTH,
  MAX_UPLOADED_FILES_SIZE,
  PAYMENT_METHOD,
  PAYMENT_PROVIDER,
  TRANSACTION_PARTY_TYPE,
  TRANSACTION_REQUISITES_TAB,
  TRANSACTION_TYPES,
  TRANSFER_TYPE
} from 'components/constants';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import {
  calculateTransactionCommission,
  checkRepeatTransactionRequest,
  confirmRepeatTransactionRequest,
  getAccountsRequest,
  getRepeatTransactionRequest,
  validateIbanRequest
} from 'services/requestAgent';
import { convertBytesToMegabytes, formatMoney, isCryptoProvider, regularFieldRegExp } from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import Alert from 'uikit/Alert/Alert';
import { AttachDoc } from 'uikit/AttachDoc/AttachDoc';
import AmountInput from 'uikit/AmountInput';
import Button from 'uikit/Button/Button';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import Input from 'uikit/Input/Input';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import { PopUp } from 'uikit/PopUp/PopUp';
import { classNames } from 'uikit/utils';
import './RepeatTransaction.scss';

// Errors of the repeat endpoint that have a dedicated explanation for the operator. The form is not built for them,
// errors of the form itself are returned in `errors`.
const KNOWN_PREFILL_ERROR_CODES = ['OPERATION_NOT_SUPPORTED', 'ACCESS_DENIED', 'NOT_FOUND'];

const NO_SERVER_ERRORS = { fields: {}, common: [] };

// Labels of the server errors that belong to no form field, `root` ones have no label
const COMMON_ERROR_LABEL_KEYS = {
  beneficiary: 'createTransaction.repeat.section.beneficiary',
  'beneficiary.type': 'createTransaction.repeat.section.beneficiary',
  requisites: 'createTransaction.repeat.section.requisites',
  tariffs: 'createTransaction.payment.commission.label',
  originalDocuments: 'createTransaction.repeat.originalDocuments.title',
  attachedDocuments: 'createTransaction.payment.attachDocument'
};

const isConveraProvider = (paymentProvider) => paymentProvider === PAYMENT_PROVIDER.CONVERA_API.name;

// A purpose can be chosen when the sender provider has purposes: the bank country ones, or all of the provider ones
// when the country is not known. It is optional, a chosen one must be in the catalog. No purposes - the purpose is not
// shown, validated and sent.
const getPurposes = (constants, values) => {
  if (!isExternalPaymentMethod(values.paymentMethod)) {
    return {};
  }

  const providerPurposes =
    constants?.paymentPurposes?.find((item) => item.transferProvider === values.paymentProvider)?.paymentPurposes || {};

  return providerPurposes[getBankCountry(values)] || Object.assign({}, ...Object.values(providerPurposes));
};

const hasPurposes = (constants, values) => Object.keys(getPurposes(constants, values)).length > 0;

const FormSection = ({ title, headerContent, children }) => (
  <section className={'repeat-transaction-section'}>
    <div className={'repeat-transaction-section-header'}>
      <span className={'repeat-transaction-section-title'}>{title}</span>
      {headerContent}
    </div>
    <div className={'repeat-transaction-section-body'}>{children}</div>
  </section>
);

FormSection.propTypes = {
  title: PropTypes.string.isRequired,
  headerContent: PropTypes.node,
  children: PropTypes.node
};

// The repeat endpoint returns only wallet numbers of the sender and an internal recipient, the form needs the accounts
const findAccountByWalletNumber = async (walletNumber) => {
  if (!walletNumber) {
    return null;
  }

  const response = await getAccountsRequest('iban', 0, 10, 'ASC', walletNumber);

  return response?.content?.find((account) => account.wallet_number === walletNumber) || null;
};

// The bank details of an IBAN are the bank data of its check - the prefilled IBAN is checked as an entered one
const checkPrefilledIban = async (original) => {
  const iban = isExternalPaymentMethod(original.paymentMethod) ? original.requisites?.iban : null;

  return iban ? validateIbanRequest(iban) : null;
};

const RepeatTransaction = ({ transactionsStore }) => {
  const i18n = useContext(i18nContext);
  const { constants } = useApplicationContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const transactionNumberFromUrl = searchParams.get('transactionNumber') || null;
  const [originalTransaction, setOriginalTransaction] = useState(null);
  const [repeatData, setRepeatData] = useState(null);
  const [isRepeatDataLoading, setIsRepeatDataLoading] = useState(false);
  const [repeatDataErrorCode, setRepeatDataErrorCode] = useState(null);
  // Errors of the last repeat, check or confirm response. They are about the values sent then, so they are shown until
  // the next check.
  const [serverErrors, setServerErrors] = useState(NO_SERVER_ERRORS);
  // The checked confirm request body and the form values it is made of, shown before the creation
  const [summary, setSummary] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // The IBAN check fills the bank details of the IBAN - the form is not sent until they are known
  const [isCheckingIban, setIsCheckingIban] = useState(false);
  const lastRequestedTransactionNumber = useRef(null);

  const isRequiredMessage = i18n.getMessage('createTransaction.validation.isRequired');
  const invalidFormatMessage = i18n.getMessage('createTransaction.validation.invalidFormat');
  const outdatedPurposeMessage = i18n.getMessage('createTransaction.repeat.validation.outdatedPurpose');

  const initialValues = useMemo(
    () =>
      fromRepeatDto(
        repeatData?.original || {},
        repeatData?.senderAccount || null,
        repeatData?.recipientAccount || null,
        repeatData?.ibanBank || null
      ),
    [repeatData]
  );

  const requiredForExternalParty = (partyType) =>
    Yup.string()
      .nullable()
      .when(['partyType', 'paymentMethod'], {
        is: (currentPartyType, paymentMethod) =>
          currentPartyType === partyType && isExternalPaymentMethod(paymentMethod),
        then: (schema) => schema.required(isRequiredMessage)
      });

  const requiredForRequisitesTabs = (...tabs) =>
    Yup.string()
      .nullable()
      .when(['activePartyTab', 'paymentMethod'], {
        is: (activePartyTab, paymentMethod) => tabs.includes(activePartyTab) && isExternalPaymentMethod(paymentMethod),
        then: (schema) => schema.required(isRequiredMessage)
      });

  const converaFormatted = (schema) =>
    schema.when('paymentProvider', {
      is: isConveraProvider,
      then: (converaSchema) => converaSchema.matches(regularFieldRegExp, invalidFormatMessage)
    });

  const form = useFormik({
    validateOnChange: false,
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      senderAccount: Yup.object().nullable().required(isRequiredMessage),
      paymentMethod: Yup.string().nullable().required(isRequiredMessage),
      recipientAccount: Yup.object()
        .nullable()
        .when('paymentMethod', {
          is: PAYMENT_METHOD.INTERNAL,
          then: (schema) => schema.required(isRequiredMessage)
        }),
      recipientWalletAddress: Yup.string()
        .nullable()
        .when('paymentMethod', {
          is: PAYMENT_METHOD.CRYPTO,
          then: (schema) => schema.required(isRequiredMessage)
        }),
      cryptoNetwork: Yup.string()
        .nullable()
        .when('paymentMethod', {
          is: PAYMENT_METHOD.CRYPTO,
          then: (schema) => schema.required(isRequiredMessage)
        }),
      partyFirstName: requiredForExternalParty(TRANSACTION_PARTY_TYPE.INDIVIDUAL),
      partyLastName: requiredForExternalParty(TRANSACTION_PARTY_TYPE.INDIVIDUAL),
      partyCompanyName: requiredForExternalParty(TRANSACTION_PARTY_TYPE.COMPANY),
      registrationNumber: Yup.string().nullable(),
      partyCountry: converaFormatted(
        Yup.string()
          .nullable()
          .when('paymentMethod', {
            is: isAdvancedPaymentMethod,
            then: (schema) => schema.required(isRequiredMessage)
          })
      ),
      partyCity: converaFormatted(Yup.string().nullable()),
      partyPostCode: converaFormatted(Yup.string().nullable()),
      partyAddress: converaFormatted(Yup.string().nullable()),
      partyState: converaFormatted(
        Yup.string()
          .nullable()
          .max(100, i18n.getMessage('createTransaction.validation.maxSize', { size: 100 }))
      ),
      iban: requiredForRequisitesTabs(TRANSACTION_REQUISITES_TAB.IBAN),
      ibanBankCountry: Yup.string().nullable(),
      ibanBankName: Yup.string().nullable(),
      ibanBic: Yup.string().nullable(),
      accountNumber: requiredForRequisitesTabs(
        TRANSACTION_REQUISITES_TAB.LOCAL_BANK_ACCOUNT,
        TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES
      ),
      sortCode: requiredForRequisitesTabs(TRANSACTION_REQUISITES_TAB.LOCAL_BANK_ACCOUNT),
      bankCountry: requiredForRequisitesTabs(TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES),
      bankName: requiredForRequisitesTabs(TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES),
      bic: requiredForRequisitesTabs(TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES),
      amount: Yup.string().required(isRequiredMessage),
      commissions: Yup.array().of(
        Yup.object().shape({
          tariffId: Yup.number().nullable().positive(i18n.getMessage('createTransaction.validation.mustBePositive')),
          amount: Yup.string()
            .nullable()
            .test('is-positive', i18n.getMessage('createTransaction.validation.mustBePositive'), (value) => {
              if (!value) return true;
              const num = parseFloat(value);
              return !isNaN(num) && num > 0;
            })
        })
      ),
      // Crypto transfers have no comment to transfer in the manual create form either
      comment: Yup.string()
        .nullable()
        .max(
          MAX_TRANSACTION_COMMENT_LENGTH,
          i18n.getMessage('createTransaction.validation.maxSize', { size: MAX_TRANSACTION_COMMENT_LENGTH })
        )
        .when('paymentMethod', {
          is: (paymentMethod) => paymentMethod !== PAYMENT_METHOD.CRYPTO,
          then: (schema) => schema.required(isRequiredMessage)
        }),
      commentToAdmin: Yup.string()
        .nullable()
        .max(
          MAX_TRANSACTION_COMMENT_LENGTH,
          i18n.getMessage('createTransaction.validation.maxSize', { size: MAX_TRANSACTION_COMMENT_LENGTH })
        ),
      // The original purpose is returned as is, even if it was removed from the catalog since then
      payment_purpose: Yup.string()
        .nullable()
        .test('purpose', outdatedPurposeMessage, function (value) {
          const purposes = getPurposes(constants, this.parent);

          return !value || Object.keys(purposes).length === 0 || value in purposes;
        })
    }),
    // The form is checked by the backend, and the transaction is created after the operator checks the request data
    // in the summary
    onSubmit: async (formValues) => {
      const { transactionNumber } = repeatData;
      const data = hasPurposes(constants, formValues) ? formValues : { ...formValues, payment_purpose: null };
      const dto = toDto(data, transactionsStore.uploadedTransactionFiles);

      try {
        const { errors: checkErrors } = await checkRepeatTransactionRequest(transactionNumber, dto);

        // The operator may have picked another transaction while the form was checked
        if (lastRequestedTransactionNumber.current !== transactionNumber) {
          return;
        }

        setServerErrors(fromErrorsDto(checkErrors, dto, data.commissions));

        if (Object.keys(checkErrors || {}).length === 0) {
          setSummary({
            dto,
            values: data,
            documents: [...(repeatData.original.documents || []), ...transactionsStore.uploadedTransactionFiles]
          });
        }
      } catch (error) {
        // The form stays as is, the error is shown by the request notification
      }
    }
  });

  const { values, errors, handleSubmit, handleChange, setFieldValue, validateField, isSubmitting } = form;
  const bankCountry = getBankCountry(values);

  // The client errors are about the current values, the server ones - about the last checked values, so the client
  // ones are shown first
  const fieldErrors = {
    ...serverErrors.fields,
    ...errors,
    commissions: (values.commissions || []).map(
      (commission, index) => errors.commissions?.[index] || serverErrors.fields.commissions?.[index]
    )
  };

  const handleSelectOriginalTransaction = async (transaction) => {
    const { transactionNumber } = transaction;

    setOriginalTransaction(transaction);
    setSearchParams({ transactionNumber }, { replace: true });
    setRepeatData(null);
    setRepeatDataErrorCode(null);
    setServerErrors(NO_SERVER_ERRORS);
    setIsRepeatDataLoading(true);
    lastRequestedTransactionNumber.current = transactionNumber;

    let nextRepeatData = null;
    let nextServerErrors = NO_SERVER_ERRORS;
    let errorCode = null;

    try {
      const response = await getRepeatTransactionRequest(transactionNumber);
      // Not finding an account is not fatal - the operator can pick it in the form. A failed IBAN check keeps the
      // original bank details, a rejected IBAN is in `errors`.
      const [senderAccount, recipientAccount, ibanBank] = await Promise.all([
        findAccountByWalletNumber(response.original.senderWallet).catch(() => null),
        findAccountByWalletNumber(response.original.requisites?.walletNumber).catch(() => null),
        checkPrefilledIban(response.original).catch(() => null)
      ]);

      nextRepeatData = { ...response, transactionNumber, senderAccount, recipientAccount, ibanBank };
      nextServerErrors = fromErrorsDto(response.errors, response.original);
    } catch (error) {
      errorCode = KNOWN_PREFILL_ERROR_CODES.includes(error?.code) ? error.code : 'UNKNOWN';
    }

    // The operator may have picked another transaction while this one was loading
    if (lastRequestedTransactionNumber.current === transactionNumber) {
      setRepeatData(nextRepeatData);
      setServerErrors(nextServerErrors);
      setRepeatDataErrorCode(errorCode);
      setIsRepeatDataLoading(false);
    }
  };

  // Tariffs of the new transaction are calculated as for a manual create, the original commission is informational.
  // The calculated amounts are sent as they are (or as the operator changed them).
  const recalculateCommissions = async (amount) => {
    if (!amount || !values.paymentMethod || !values.senderAccount) {
      return;
    }

    const customCommissions = values.commissions?.filter((c) => !c.tariffId && c.isCustom) || [];

    try {
      const response = await calculateTransactionCommission({
        walletNumber: values.senderAccount.wallet_number,
        paymentMethod: values.paymentMethod,
        transactionType: TRANSACTION_TYPES.OUTGOING,
        transferType: TRANSFER_TYPE.NORMAL,
        amount,
        ...(bankCountry ? { bankCountry } : {})
      });

      const tariffCommissions = (response.appliedTariffs || []).map((tariff) => ({
        tariffId: tariff.tariffId,
        tariffName: tariff.systemTariffKey || `Tariff ${tariff.tariffId}`,
        // Amounts of the form are strings, as typed in the inputs. A zero amount is an empty one - it is not sent
        amount: tariff.amount ? String(tariff.amount) : '',
        currency: values.currency || 'EUR',
        feeType: tariff.feeType,
        // Rule values are shown under the tariff name to compare with the original commission
        fixedValue: tariff.fixedValue,
        percentValue: tariff.percentValue,
        min: tariff.min,
        max: tariff.max,
        systemTariffKey: tariff.systemTariffKey,
        highRiskCountries: tariff.highRiskCountries
      }));

      const commissions = [...tariffCommissions, ...customCommissions];
      await setFieldValue('commissions', commissions.length ? commissions : [createCustomCommission(values.currency)]);
    } catch (error) {
      // Keep the current commissions - the operator can still adjust them manually
    }
  };

  useEffect(() => {
    recalculateCommissions(values.amount);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.paymentMethod, values.senderAccount, bankCountry]);

  useEffect(() => {
    if (values.senderAccount) {
      validateField('senderAccount');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.senderAccount]);

  useEffect(() => {
    if (values.recipientAccount) {
      validateField('recipientAccount');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.recipientAccount]);

  const handleChangeValue = (name, value) => setFieldValue(name, value);

  // The account control also sets the provider, the payment method and the currency of the picked account. They are
  // defined by the sender wallet, so only the account is taken from the recipient
  const handleChangeRecipientAccount = (name, value) => {
    if (name === 'recipientAccount') {
      setFieldValue(name, value);
    }
  };

  const handleAmountBlur = async () => {
    let cleanedAmount = values.amount;
    if (cleanedAmount && cleanedAmount.endsWith('.')) {
      cleanedAmount = cleanedAmount.slice(0, -1);
      await setFieldValue('amount', cleanedAmount);
    }

    validateField('amount');
    await recalculateCommissions(cleanedAmount);
  };

  const handleChangeCommission = (index, value) => {
    const commissions = [...values.commissions];
    commissions[index] = { ...commissions[index], amount: value };
    setFieldValue('commissions', commissions);
  };

  const handleDeleteCommission = (index) => {
    const commissions = [...values.commissions];
    commissions.splice(index, 1);
    setFieldValue('commissions', commissions);
  };

  const handleAddCommission = () => {
    setFieldValue('commissions', [...(values.commissions || []), createCustomCommission(values.currency)]);
  };

  const handleToggleOriginalDocument = (documentId) => {
    const { originalDocuments } = values;

    setFieldValue(
      'originalDocuments',
      originalDocuments.includes(documentId)
        ? originalDocuments.filter((id) => id !== documentId)
        : [...originalDocuments, documentId]
    );
  };

  const renderRepeatDataError = () => {
    if (!repeatDataErrorCode) {
      return null;
    }

    return (
      <Alert
        className={'repeat-transaction-alert'}
        type={'warning'}
        message={i18n.getMessage(`createTransaction.repeat.error.${repeatDataErrorCode}`)}
      />
    );
  };

  // Server errors that belong to no form field, e.g. a provider rejection
  const renderCommonErrors = () =>
    serverErrors.common.map(({ path, message }) => {
      const labelKey = COMMON_ERROR_LABEL_KEYS[path];
      const label = labelKey ? i18n.getMessage(labelKey) : path;

      return (
        <Alert
          key={path}
          className={'repeat-transaction-alert'}
          type={'warning'}
          message={path === 'root' ? message : `${label}: ${message}`}
        />
      );
    });

  const renderPaymentPurpose = () => {
    const purposes = getPurposes(constants, values);

    if (Object.keys(purposes).length === 0) {
      return null;
    }

    const isOutdatedPurpose = !!values.payment_purpose && !(values.payment_purpose in purposes);
    const purposeOptions = [
      ...(isOutdatedPurpose ? [{ key: values.payment_purpose, value: values.payment_purpose }] : []),
      ...Object.entries(purposes).map(([key, description]) => ({ key, value: `${key} - ${description}` }))
    ];

    // The purpose is optional - it is cleared by erasing its text
    return (
      <InputDropDownSearch
        isClearValueAfterClickOutside
        className={'repeat-transaction-search-select'}
        label={i18n.getMessage('createTransaction.payment.paymentPurpose.label')}
        placeholder={i18n.getMessage('createTransaction.payment.paymentPurpose.placeholder')}
        id={'payment_purpose'}
        name={'payment_purpose'}
        value={values.payment_purpose}
        options={purposeOptions}
        onChange={handleChangeValue}
        onBlur={() => validateField('payment_purpose')}
        error={fieldErrors.payment_purpose || (isOutdatedPurpose ? outdatedPurposeMessage : undefined)}
      />
    );
  };

  const renderRequisites = () => {
    if (values.paymentMethod === PAYMENT_METHOD.INTERNAL) {
      const recipientWalletNumber = repeatData.original.requisites?.walletNumber;

      return (
        <>
          {!values.recipientAccount && recipientWalletNumber && (
            <Alert
              className={'repeat-transaction-alert'}
              type={'warning'}
              message={i18n.getMessage('createTransaction.repeat.recipientAccountNotFound', {
                walletNumber: recipientWalletNumber
              })}
            />
          )}
          <InternalTransactionPartyControl
            isValueControlled
            account={values.recipientAccount}
            paymentProvider={values.paymentProvider}
            paymentMethod={values.paymentMethod}
            handleChangeValue={handleChangeRecipientAccount}
            property={'recipientAccount'}
            accountLabel={i18n.getMessage('createTransaction.payment.toAccount')}
            isShowPaymentMethod={false}
            errors={fieldErrors}
          />
        </>
      );
    }

    if (values.paymentMethod === PAYMENT_METHOD.CRYPTO) {
      return (
        <div className={'repeat-transaction-row-block'}>
          <Input
            isRequired
            className={'repeat-transaction-input'}
            label={i18n.getMessage('createTransaction.repeat.cryptoAddress')}
            placeholder={i18n.getMessage('createTransaction.crypto.cryptoAddress.placeholder')}
            id={'recipientWalletAddress'}
            name={'recipientWalletAddress'}
            value={values.recipientWalletAddress}
            onChange={handleChange}
            onBlur={() => validateField('recipientWalletAddress')}
            error={fieldErrors.recipientWalletAddress}
          />
          <Input
            isRequired
            className={'repeat-transaction-input repeat-transaction-crypto-network'}
            label={i18n.getMessage('createTransaction.repeat.cryptoNetwork')}
            id={'cryptoNetwork'}
            name={'cryptoNetwork'}
            value={values.cryptoNetwork}
            onChange={handleChange}
            onBlur={() => validateField('cryptoNetwork')}
            error={fieldErrors.cryptoNetwork}
          />
        </div>
      );
    }

    return (
      <RequisitesSection
        values={values}
        errors={fieldErrors}
        partyLabel={i18n.getMessage('createTransaction.repeat.section.beneficiary')}
        isCheckingIban={isCheckingIban}
        handleChange={handleChange}
        setFieldValue={setFieldValue}
        validateField={validateField}
        onIbanCheckingChange={setIsCheckingIban}
      />
    );
  };

  // Grouped as in the API model: `requisites` of any recipient and `beneficiary` (with address) of an external one
  const renderRecipient = () => {
    if (!values.paymentMethod) {
      return null;
    }

    return (
      <>
        <FormSection
          title={i18n.getMessage('createTransaction.repeat.section.requisites')}
          headerContent={
            isExternalPaymentMethod(values.paymentMethod) && (
              <SectionSwitch
                options={getRequisitesKindOptions(i18n)}
                value={values.activePartyTab}
                onChange={(kind) => setFieldValue('activePartyTab', kind)}
              />
            )
          }
        >
          {renderRequisites()}
        </FormSection>

        {isExternalPaymentMethod(values.paymentMethod) && (
          <FormSection
            title={i18n.getMessage('createTransaction.repeat.section.beneficiary')}
            headerContent={
              <SectionSwitch
                options={getPartyTypeOptions(i18n)}
                value={values.partyType}
                onChange={(partyType) => setFieldValue('partyType', partyType)}
              />
            }
          >
            <BeneficiarySection
              values={values}
              errors={fieldErrors}
              isCountryRequired={isAdvancedPaymentMethod(values.paymentMethod)}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              validateField={validateField}
            />
          </FormSection>
        )}
      </>
    );
  };

  // A checked document is copied to the new transaction
  const renderOriginalDocuments = () => {
    const documents = repeatData.original.documents || [];

    if (documents.length === 0) {
      return null;
    }

    return (
      <div className={'repeat-transaction-original-documents'}>
        <span className={'repeat-transaction-original-documents-title'}>
          {i18n.getMessage('createTransaction.repeat.originalDocuments.title')}
        </span>
        <Alert type={'info'} message={i18n.getMessage('createTransaction.repeat.originalDocuments.hint')} />
        <ul className={'repeat-transaction-original-documents-list'}>
          {documents.map((document) => {
            const error = fieldErrors.originalDocuments?.[document.id];

            return (
              <li key={document.id}>
                <div className={'repeat-transaction-original-documents-item'}>
                  <CheckBox
                    className={'repeat-transaction-original-document-checkbox'}
                    isActive={values.originalDocuments.includes(document.id)}
                    showCheckBox
                    onChange={() => handleToggleOriginalDocument(document.id)}
                  />
                  {/* A plain button - the uikit one has a white text and a fixed width */}
                  <button
                    type={'button'}
                    className={'repeat-transaction-original-document'}
                    onClick={() => transactionsStore.downloadPaymentTransactionDocument(document.id)}
                    disabled={transactionsStore.isLoading}
                  >
                    <DownloadIcon className={'repeat-transaction-original-document-icon'} aria-hidden={'true'} />
                    <span>{document.name}</span>
                  </button>
                </div>
                {error && <span className={'repeat-transaction-original-document-error'}>{error}</span>}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  // Errors of the uploaded documents are shown with their names - the attachment field has one error for all files
  const getAttachedDocumentsError = () =>
    transactionsStore.uploadedTransactionFiles
      .filter((file) => fieldErrors.attachedDocuments?.[file.id])
      .map((file) => `${file.name}: ${fieldErrors.attachedDocuments[file.id]}`)
      .join('; ') || undefined;

  const renderForm = () => {
    const amountPrecision = isCryptoProvider(values.paymentProvider)
      ? CRYPTO_CURRENCY_PRECISION
      : COMMON_CURRENCY_PRECISION;
    const amountUnit = [values.currency, values.senderAccount?.network].filter(Boolean).join(', ');
    const isShowBalance = values.senderAccount?.available !== null && values.senderAccount?.available !== undefined;

    return (
      <form className={'repeat-transaction-form'} onSubmit={handleSubmit}>
        {!values.senderAccount && (
          <Alert
            className={'repeat-transaction-alert'}
            type={'warning'}
            message={i18n.getMessage('createTransaction.repeat.senderAccountNotFound', {
              walletNumber: repeatData.original.senderWallet || '-'
            })}
          />
        )}
        <div className={classNames({ 'repeat-transaction-sender': true, 'with-balance': isShowBalance })}>
          <InternalTransactionPartyControl
            isValueControlled
            account={values.senderAccount}
            paymentProvider={values.paymentProvider}
            paymentMethod={values.paymentMethod}
            handleChangeValue={handleChangeValue}
            property={'senderAccount'}
            accountLabel={i18n.getMessage('createTransaction.payment.fromAccount')}
            isShowPaymentProvider={false}
            isShowPaymentMethod={true}
            errors={fieldErrors}
          />
          {isShowBalance && (
            <span className={'repeat-transaction-available-balance'}>
              {i18n.getMessage('createTransaction.repeat.availableBalance', {
                amount: formatMoney(values.senderAccount.available, amountPrecision),
                currency: values.senderAccount.currency
              })}
            </span>
          )}
        </div>

        {renderRecipient()}

        <div className={'repeat-transaction-block-wrapper'}>
          <div className={'repeat-transaction-row-block'}>
            {/* Currency (and network of a crypto wallet) is defined by the sender account, so it is shown in
                the label instead of a separate field */}
            <AmountInput
              isRequired
              className={'repeat-transaction-input'}
              label={
                amountUnit
                  ? i18n.getMessage('createTransaction.repeat.amount', { unit: amountUnit })
                  : i18n.getMessage('createTransaction.payment.amount.label')
              }
              placeholder={i18n.getMessage('createTransaction.payment.amount.placeholder')}
              name={'amount'}
              value={values.amount}
              precision={amountPrecision}
              returnValueType={AMOUNT_INPUT_VALUE_TYPE.STRING}
              onChange={handleChange}
              onBlur={handleAmountBlur}
              error={fieldErrors.amount}
            />
            {renderPaymentPurpose()}
          </div>

          <CommissionsList
            commissions={values.commissions || []}
            currency={values.currency}
            onChange={handleChangeCommission}
            onBlur={(index) => validateField(`commissions[${index}].amount`)}
            onDelete={handleDeleteCommission}
            onAdd={handleAddCommission}
            errors={fieldErrors.commissions}
            // Commissions are always charged in the transaction currency - shown once in the title, as for the amount
            amountTitle={
              amountUnit ? i18n.getMessage('createTransaction.repeat.amount', { unit: amountUnit }) : undefined
            }
            isShowRowCurrency={!amountUnit}
            isShowCount={false}
            // Tariff rules of the new commissions are shown as for the original ones, to see why they differ
            isShowTariffRule
            originalCommission={repeatData.original.commission || undefined}
            precision={amountPrecision}
          />

          <Input
            isRequired={values.paymentMethod !== PAYMENT_METHOD.CRYPTO}
            type={'textarea'}
            rows={2}
            className={'repeat-transaction-textarea'}
            label={i18n.getMessage('createTransaction.payment.commentToTransfer')}
            id={'comment'}
            name={'comment'}
            value={values.comment}
            onChange={handleChange}
            onBlur={() => validateField('comment')}
            error={fieldErrors.comment}
            max={MAX_TRANSACTION_COMMENT_LENGTH}
            subText={i18n.getMessage('createTransaction.payment.symbolsLeft', {
              symbolsLeft: MAX_TRANSACTION_COMMENT_LENGTH - (values.comment?.length || 0)
            })}
          />

          <Input
            type={'textarea'}
            rows={2}
            className={'repeat-transaction-textarea'}
            label={i18n.getMessage('createTransaction.payment.commentToAdmin')}
            id={'commentToAdmin'}
            name={'commentToAdmin'}
            value={values.commentToAdmin}
            onChange={handleChange}
            onBlur={() => validateField('commentToAdmin')}
            error={fieldErrors.commentToAdmin}
            max={MAX_TRANSACTION_COMMENT_LENGTH}
            subText={i18n.getMessage('createTransaction.payment.symbolsLeft', {
              symbolsLeft: MAX_TRANSACTION_COMMENT_LENGTH - (values.commentToAdmin?.length || 0)
            })}
          />

          {renderOriginalDocuments()}

          <AttachDoc
            label={i18n.getMessage('createTransaction.payment.attachDocument')}
            className={'repeat-transaction-attachment'}
            type={'file'}
            size={MAX_UPLOADED_FILES_SIZE}
            files={transactionsStore.uploadedTransactionFiles}
            onChange={transactionsStore.uploadTransactionAttachments}
            onRemoveFiles={transactionsStore.removeTransactionAttachment}
            error={getAttachedDocumentsError()}
            isDisabled={transactionsStore.isLoading || transactionsStore.isTransactionFileUploading}
            hintText={i18n.getMessage('attachDoc.hint.text', {
              maxFilesSize: convertBytesToMegabytes(MAX_UPLOADED_FILES_SIZE)
            })}
          />
        </div>

        {renderCommonErrors()}

        <div className={'repeat-transaction-submit-button-wrapper'}>
          <Button
            className={'repeat-transaction-submit-button'}
            type={'primary'}
            roleType={'submit'}
            size={'large'}
            onClick={() => {}}
            isDisabled={isSubmitting || isCheckingIban || isConfirming || transactionsStore.isLoading}
          >
            {isSubmitting || transactionsStore.isLoading ? (
              <Loader />
            ) : (
              i18n.getMessage('createTransaction.repeat.button.createTransaction')
            )}
          </Button>
        </div>
      </form>
    );
  };

  const handleCloseSummary = () => {
    if (!isConfirming) {
      setSummary(null);
    }
  };

  // There is no double submit protection on the backend - the summary is not closed and its buttons are disabled while
  // the transaction is being created. The form is not sent either: the summary still closes on Escape (PopUp keeps the
  // first onClose), and a new check would open a new summary.
  const handleConfirmSummary = async () => {
    const { transactionNumber } = repeatData;

    setIsConfirming(true);

    try {
      const newTransaction = await confirmRepeatTransactionRequest(transactionNumber, summary.dto);

      toast.success(
        i18n.getMessage('createTransaction.repeat.toast.created', {
          transactionNumber: newTransaction.transactionNumber
        })
      );
      navigate(`${ROUTE_PATHS.TRANSACTIONS}/${newTransaction.transactionNumber}`);
    } catch (error) {
      // Nothing is created on a validation failure - the form shows why. Any other failure (e.g. a timeout) does not
      // tell if the transaction was created, so it is not confirmed again from the same summary.
      setServerErrors(
        error?.errors
          ? fromErrorsDto(error.errors, summary.dto, summary.values.commissions)
          : {
              fields: {},
              common: [{ path: 'root', message: i18n.getMessage('createTransaction.repeat.error.confirmFailed') }]
            }
      );
      setSummary(null);
    } finally {
      setIsConfirming(false);
    }
  };

  const renderSummary = () => {
    if (!summary) {
      return null;
    }

    const { dto, values: summaryValues, documents } = summary;
    const purposeDescription = dto.purpose && getPurposes(constants, summaryValues)[dto.purpose];

    return (
      <PopUp show alignOnCenter onClose={handleCloseSummary}>
        <RepeatTransactionSummary
          dto={dto}
          currency={summaryValues.currency}
          precision={
            isCryptoProvider(summaryValues.paymentProvider) ? CRYPTO_CURRENCY_PRECISION : COMMON_CURRENCY_PRECISION
          }
          commissions={summaryValues.commissions || []}
          documents={documents}
          purposeLabel={purposeDescription ? `${dto.purpose} - ${purposeDescription}` : undefined}
          isLoading={isConfirming}
          onConfirm={handleConfirmSummary}
          onClose={handleCloseSummary}
        />
      </PopUp>
    );
  };

  return (
    <div className={'repeat-transaction-wrapper'}>
      <RepeatTransactionAsyncSelect
        value={originalTransaction}
        transactionNumber={transactionNumberFromUrl}
        onChange={handleSelectOriginalTransaction}
      />

      {isRepeatDataLoading && <Loader className={'repeat-transaction-loader'} />}
      {renderRepeatDataError()}
      {!isRepeatDataLoading && repeatData && renderForm()}
      {renderSummary()}
    </div>
  );
};

RepeatTransaction.propTypes = {
  transactionsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  transactionsStore: stores.transactionsStore
}))(observer(RepeatTransaction));
